import React, { useState, useMemo, useCallback } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from "recharts";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";

import {
  Calculator,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  Target,
  Info,
  Grid3X3,
  TrendingUp,
  Shuffle,
  Download,
  CheckCircle2,
  XCircle,
  History,
  Zap,
  Award,
  Brain
} from 'lucide-react';
import "@/App.css";

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Solve using Cramer's Rule
const solveCramersRule = (a1, b1, c1, a2, b2, c2) => {
  const det = a1 * b2 - a2 * b1;
  
  if (Math.abs(det) < 1e-10) {
    const det1 = c1 * b2 - c2 * b1;
    const det2 = a1 * c2 - a2 * c1;
    
    if (Math.abs(det1) < 1e-10 && Math.abs(det2) < 1e-10) {
      return { type: 'infinite', x: null, y: null, det, dx: det1, dy: det2 };
    }
    return { type: 'none', x: null, y: null, det, dx: det1, dy: det2 };
  }
  
  const dx = c1 * b2 - c2 * b1;
  const dy = a1 * c2 - a2 * c1;
  const x = dx / det;
  const y = dy / det;
  
  return { type: 'unique', x, y, det, dx, dy };
};

// Solve using Substitution Method
const solveSubstitution = (a1, b1, c1, a2, b2, c2) => {
  const steps = [];
  
  // Express x from equation 1 if a1 ≠ 0
  if (Math.abs(a1) > 1e-10) {
    steps.push(`From Equation 1: ${a1}x + ${b1}y = ${c1}`);
    steps.push(`Solve for x: x = (${c1} - ${b1}y) / ${a1}`);
    steps.push(`Substitute into Equation 2: ${a2}((${c1} - ${b1}y) / ${a1}) + ${b2}y = ${c2}`);
    
    // Simplify
    const coef = (a2 * b1) / a1 - b2;
    const const_term = c2 - (a2 * c1) / a1;
    
    if (Math.abs(coef) < 1e-10) {
      if (Math.abs(const_term) < 1e-10) {
        return { type: 'infinite', steps, x: null, y: null };
      }
      return { type: 'none', steps, x: null, y: null };
    }
    
    const y = const_term / coef;
    const x = (c1 - b1 * y) / a1;
    
    steps.push(`Solving: ${coef.toFixed(4)}y = ${const_term.toFixed(4)}`);
    steps.push(`y = ${y.toFixed(4)}`);
    steps.push(`Substitute back: x = (${c1} - ${b1}·${y.toFixed(4)}) / ${a1} = ${x.toFixed(4)}`);
    
    return { type: 'unique', x, y, steps };
  }
  
  return solveCramersRule(a1, b1, c1, a2, b2, c2);
};

// Solve using Elimination Method
const solveElimination = (a1, b1, c1, a2, b2, c2) => {
  const steps = [];
  
  steps.push(`Equation 1: ${a1}x + ${b1}y = ${c1}`);
  steps.push(`Equation 2: ${a2}x + ${b2}y = ${c2}`);
  
  // Eliminate x by multiplying equations
  const mult1 = a2;
  const mult2 = a1;
  
  steps.push(`Multiply Eq1 by ${mult1}: ${a1*mult1}x + ${b1*mult1}y = ${c1*mult1}`);
  steps.push(`Multiply Eq2 by ${mult2}: ${a2*mult2}x + ${b2*mult2}y = ${c2*mult2}`);
  steps.push(`Subtract to eliminate x:`);
  
  const new_b = b1 * mult1 - b2 * mult2;
  const new_c = c1 * mult1 - c2 * mult2;
  
  if (Math.abs(new_b) < 1e-10) {
    if (Math.abs(new_c) < 1e-10) {
      return { type: 'infinite', steps, x: null, y: null };
    }
    return { type: 'none', steps, x: null, y: null };
  }
  
  const y = new_c / new_b;
  const x = (c1 - b1 * y) / a1;
  
  steps.push(`${new_b.toFixed(4)}y = ${new_c.toFixed(4)}`);
  steps.push(`y = ${y.toFixed(4)}`);
  steps.push(`Substitute into Eq1: ${a1}x + ${b1}·${y.toFixed(4)} = ${c1}`);
  steps.push(`x = ${x.toFixed(4)}`);
  
  return { type: 'unique', x, y, steps };
};

const generateLineData = (a, b, c, xRange = [-10, 10], steps = 100) => {
  const data = [];
  const step = (xRange[1] - xRange[0]) / steps;
  
  for (let x = xRange[0]; x <= xRange[1]; x += step) {
    if (Math.abs(b) > 1e-10) {
      const y = (c - a * x) / b;
      if (isFinite(y) && Math.abs(y) < 20) {
        data.push({ x: parseFloat(x.toFixed(2)), y: parseFloat(y.toFixed(4)) });
      }
    }
  }
  return data;
};

const GraphExplanation = ({ solution, eq1, eq2 }) => {
  const getSlopeInfo = (a, b, c) => {
    if (Math.abs(b) < 1e-10) return { slope: 'undefined', intercept: null, type: 'vertical' };
    const slope = -a / b;
    const yIntercept = c / b;
    const xIntercept = Math.abs(a) > 1e-10 ? c / a : null;
    return { slope: slope.toFixed(4), intercept: yIntercept.toFixed(4), xIntercept: xIntercept?.toFixed(4), type: 'normal' };
  };
  
  const line1Info = getSlopeInfo(eq1.a, eq1.b, eq1.c);
  const line2Info = getSlopeInfo(eq2.a, eq2.b, eq2.c);
  
  return (
    <div className="space-y-3 p-4 bg-muted/30 rounded-xl">
      <h4 className="font-semibold text-foreground flex items-center gap-2">
        <Target className="w-4 h-4 text-emerald-600" />
        Graph Analysis
      </h4>
      
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-1 rounded bg-emerald-500" />
            <span className="font-medium text-emerald-700 dark:text-emerald-400">Line 1</span>
          </div>
          <p className="text-muted-foreground text-xs">
            Slope: <span className="font-mono text-foreground">{line1Info.slope}</span><br />
            Y-intercept: <span className="font-mono text-foreground">{line1Info.intercept || 'N/A'}</span><br />
            X-intercept: <span className="font-mono text-foreground">{line1Info.xIntercept || 'N/A'}</span>
          </p>
        </div>
        
        <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-1 rounded bg-orange-500" />
            <span className="font-medium text-orange-700 dark:text-orange-400">Line 2</span>
          </div>
          <p className="text-muted-foreground text-xs">
            Slope: <span className="font-mono text-foreground">{line2Info.slope}</span><br />
            Y-intercept: <span className="font-mono text-foreground">{line2Info.intercept || 'N/A'}</span><br />
            X-intercept: <span className="font-mono text-foreground">{line2Info.xIntercept || 'N/A'}</span>
          </p>
        </div>
      </div>
      
      <div className="p-3 rounded-lg bg-background">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-sm">Interpretation:</span>
            <p className="text-muted-foreground text-xs mt-1">
              {solution.type === 'unique' 
                ? `Lines intersect at (${solution.x.toFixed(2)}, ${solution.y.toFixed(2)}). Different slopes mean exactly one solution.`
                : solution.type === 'infinite'
                ? `Lines are identical with slope ${line1Info.slope}. Every point is a solution.`
                : `Lines are parallel with slopes ${line1Info.slope} and ${line2Info.slope}. No intersection exists.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Verification Component
const VerificationCheck = ({ solution, a1, b1, c1, a2, b2, c2 }) => {
  if (solution.type !== 'unique') return null;
  
  const check1 = a1 * solution.x + b1 * solution.y;
  const check2 = a2 * solution.x + b2 * solution.y;
  const isValid1 = Math.abs(check1 - c1) < 1e-8;
  const isValid2 = Math.abs(check2 - c2) < 1e-8;
  
  return (
    <div className="p-4 rounded-xl bg-background border">
      <h4 className="font-semibold text-foreground flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-4 h-4 text-green-500" />
        Solution Verification
      </h4>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Equation 1: {a1}({solution.x.toFixed(4)}) + {b1}({solution.y.toFixed(4)}) = {check1.toFixed(4)}</span>
          {isValid1 ? <Check className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Equation 2: {a2}({solution.x.toFixed(4)}) + {b2}({solution.y.toFixed(4)}) = {check2.toFixed(4)}</span>
          {isValid2 ? <Check className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
        </div>
        {isValid1 && isValid2 && (
          <p className="text-green-600 dark:text-green-400 text-xs mt-2">✓ Solution verified correct!</p>
        )}
      </div>
    </div>
  );
};

function LinearEquationSolver() {
  const [a1, setA1] = useState(2);
  const [b1, setB1] = useState(1);
  const [c1, setC1] = useState(5);
  const [a2, setA2] = useState(1);
  const [b2, setB2] = useState(-1);
  const [c2, setC2] = useState(1);
  const [method, setMethod] = useState('cramers');
  const [history, setHistory] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const solution = useMemo(() => {
    let result;
    switch(method) {
      case 'cramers':
        result = solveCramersRule(a1, b1, c1, a2, b2, c2);
        break;
      case 'substitution':
        result = solveSubstitution(a1, b1, c1, a2, b2, c2);
        break;
      case 'elimination':
        result = solveElimination(a1, b1, c1, a2, b2, c2);
        break;
      default:
        result = solveCramersRule(a1, b1, c1, a2, b2, c2);
    }
    return result;
  }, [a1, b1, c1, a2, b2, c2, method]);

  const line1Data = useMemo(() => generateLineData(a1, b1, c1), [a1, b1, c1]);
  const line2Data = useMemo(() => generateLineData(a2, b2, c2), [a2, b2, c2]);

  const handleReset = useCallback(() => {
    setA1(2); setB1(1); setC1(5);
    setA2(1); setB2(-1); setC2(1);
  }, []);

  const handleRandomExample = useCallback(() => {
    const rand = () => Math.floor(Math.random() * 10) - 5;
    setA1(rand() || 1);
    setB1(rand() || 1);
    setC1(rand() || 1);
    setA2(rand() || 1);
    setB2(rand() || 1);
    setC2(rand() || 1);
  }, []);

  const handleSaveToHistory = useCallback(() => {
    if (solution.type === 'unique') {
      setHistory(prev => [
        { eq1: `${a1}x + ${b1}y = ${c1}`, eq2: `${a2}x + ${b2}y = ${c2}`, x: solution.x, y: solution.y, timestamp: new Date() },
        ...prev.slice(0, 4)
      ]);
    }
  }, [a1, b1, c1, a2, b2, c2, solution]);

  const handleExportSolution = useCallback(() => {
    const content = `Linear Equation System Solution\n\nEquations:\n${a1}x + ${b1}y = ${c1}\n${a2}x + ${b2}y = ${c2}\n\nMethod: ${method}\n\nSolution:\n${solution.type === 'unique' ? `x = ${solution.x}\ny = ${solution.y}` : solution.type}\n\nGenerated: ${new Date().toLocaleString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'linear-equation-solution.txt';
    a.click();
  }, [a1, b1, c1, a2, b2, c2, solution, method]);

  const formatEquation = (a, b, c) => {
    let eq = '';
    if (a !== 0) eq += `${a === 1 ? '' : a === -1 ? '-' : a}x`;
    if (b !== 0) {
      if (eq && b > 0) eq += ' + ';
      else if (eq && b < 0) eq += ' - ';
      else if (b < 0) eq += '-';
      eq += `${Math.abs(b) === 1 ? '' : Math.abs(b)}y`;
    }
    if (!eq) eq = '0';
    eq += ` = ${c}`;
    return eq;
  };

  return (
    <HelmetProvider>
      <div className="container mx-auto px-4 py-8">
        <Helmet>
          <title>Advanced Linear Equation Solver - Multiple Methods with Visualization</title>
          <meta name="description" content="Solve systems of linear equations using Cramer's Rule, Substitution, or Elimination methods with interactive graphs and step-by-step solutions." />
        </Helmet>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Grid3X3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Linear Equation Solver</h1>
              <p className="text-muted-foreground">Solve 2×2 systems with multiple methods and full visualization</p>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Calculator Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-foreground">Input System</span>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={handleRandomExample}
                    title="Random Example"
                  >
                    <Shuffle className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={handleReset} 
                    title="Reset"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {/* Equation 1 */}
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <Label className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-3 block">Equation 1</Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      type="number"
                      value={a1}
                      onChange={(e) => setA1(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                    <span className="font-mono text-foreground">x +</span>
                    <Input
                      type="number"
                      value={b1}
                      onChange={(e) => setB1(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                    <span className="font-mono text-foreground">y =</span>
                    <Input
                      type="number"
                      value={c1}
                      onChange={(e) => setC1(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 font-mono text-center">{formatEquation(a1, b1, c1)}</p>
                </div>

                {/* Equation 2 */}
                <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <Label className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-3 block">Equation 2</Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      type="number"
                      value={a2}
                      onChange={(e) => setA2(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                    <span className="font-mono text-foreground">x +</span>
                    <Input
                      type="number"
                      value={b2}
                      onChange={(e) => setB2(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                    <span className="font-mono text-foreground">y =</span>
                    <Input
                      type="number"
                      value={c2}
                      onChange={(e) => setC2(parseFloat(e.target.value) || 0)}
                      className="w-16 text-center font-mono h-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 font-mono text-center">{formatEquation(a2, b2, c2)}</p>
                </div>

                {/* Solution Method */}
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Solution Method</Label>
                  <Tabs value={method} onValueChange={setMethod}>
                    <TabsList className="w-full grid grid-cols-3">
                      <TabsTrigger value="cramers" className="text-xs">Cramer's</TabsTrigger>
                      <TabsTrigger value="substitution" className="text-xs">Substitution</TabsTrigger>
                      <TabsTrigger value="elimination" className="text-xs">Elimination</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* Quick Examples */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => { setA1(2); setB1(1); setC1(5); setA2(1); setB2(-1); setC2(1); }}
                    >
                      Unique
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => { setA1(2); setB1(4); setC1(6); setA2(1); setB2(2); setC2(3); }}
                    >
                      Infinite
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => { setA1(2); setB1(4); setC1(6); setA2(1); setB2(2); setC2(5); }}
                    >
                      No Sol.
                    </Button>
                  </div>
                </div>

                {/* Results */}
                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-foreground">Solution</Label>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={handleSaveToHistory}
                        disabled={solution.type !== 'unique'}
                        title="Save to History"
                      >
                        <History className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={handleExportSolution}
                        title="Export Solution"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${solution.x}-${solution.y}-${solution.type}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                      className={`rounded-xl p-4 ${
                        solution.type === 'unique' 
                          ? 'bg-emerald-500/10 border border-emerald-500/30' 
                          : solution.type === 'infinite' 
                          ? 'bg-blue-500/10 border border-blue-500/30' 
                          : 'bg-red-500/10 border border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {solution.type === 'unique' ? 'Unique Solution' : solution.type === 'infinite' ? 'Infinite Solutions' : 'No Solution'}
                        </Badge>
                        {solution.type === 'unique' && <CopyButton text={`(${solution.x.toFixed(4)}, ${solution.y.toFixed(4)})`} />}
                      </div>
                      {solution.type === 'unique' ? (
                        <div className="space-y-1">
                          <p className="font-mono text-lg font-semibold text-foreground">
                            x = <span className="text-emerald-600">{solution.x.toFixed(6)}</span>
                          </p>
                          <p className="font-mono text-lg font-semibold text-foreground">
                            y = <span className="text-emerald-600">{solution.y.toFixed(6)}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">Point: ({solution.x.toFixed(4)}, {solution.y.toFixed(4)})</p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          {solution.type === 'infinite' 
                            ? 'Lines are identical. All points on the line are solutions.' 
                            : 'Lines are parallel. They never intersect.'}
                        </p>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* History */}
                {history.length > 0 && (
                  <div className="pt-4 border-t border-border">
                    <Label className="text-xs text-muted-foreground mb-2 block">Recent Solutions</Label>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {history.map((item, idx) => (
                        <div key={idx} className="text-xs p-2 rounded bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                          onClick={() => {
                            const match1 = item.eq1.match(/([\-\d.]+)x \+ ([\-\d.]+)y = ([\-\d.]+)/);
                            const match2 = item.eq2.match(/([\-\d.]+)x \+ ([\-\d.]+)y = ([\-\d.]+)/);
                            if (match1 && match2) {
                              setA1(parseFloat(match1[1]));
                              setB1(parseFloat(match1[2]));
                              setC1(parseFloat(match1[3]));
                              setA2(parseFloat(match2[1]));
                              setB2(parseFloat(match2[2]));
                              setC2(parseFloat(match2[3]));
                            }
                          }}
                        >
                          <p className="text-muted-foreground font-mono">({item.x.toFixed(2)}, {item.y.toFixed(2)})</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Center/Right Column - Visualization & Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visualization Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="rounded-2xl p-6 border bg-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <Info className="w-4 h-4 text-emerald-600" />
                    Interactive Graph
                    <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                  </h2>
                </div>

                <div className="h-[400px] bg-muted/30 rounded-xl p-4 border">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="x" 
                        type="number"
                        domain={[-10, 10]}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        label={{ value: 'x', position: 'insideBottomRight', offset: -5 }}
                      />
                      <YAxis 
                        type="number"
                        domain={[-10, 10]}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        label={{ value: 'y', position: 'insideTopLeft', offset: 10 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                      <ReferenceLine x={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} />
                      <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} />
                      
                      <Line
                        data={line1Data}
                        type="monotone"
                        dataKey="y"
                        stroke="#10b981"
                        dot={false}
                        strokeWidth={3}
                        name="Equation 1"
                      />
                      
                      <Line
                        data={line2Data}
                        type="monotone"
                        dataKey="y"
                        stroke="#f97316"
                        dot={false}
                        strokeWidth={3}
                        name="Equation 2"
                      />
                      
                      {solution.type === 'unique' && (
                        <ReferenceDot
                          x={solution.x}
                          y={solution.y}
                          r={8}
                          fill="#8b5cf6"
                          stroke="white"
                          strokeWidth={3}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-1 rounded bg-emerald-500" />
                    <span className="text-sm text-muted-foreground">Equation 1</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-1 rounded bg-orange-500" />
                    <span className="text-sm text-muted-foreground">Equation 2</span>
                  </div>
                  {solution.type === 'unique' && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-violet-500" />
                      <span className="text-sm text-muted-foreground">Intersection</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 space-y-3">
                  <GraphExplanation 
                    solution={solution}
                    eq1={{ a: a1, b: b1, c: c1 }}
                    eq2={{ a: a2, b: b2, c: c2 }}
                  />
                  <VerificationCheck 
                    solution={solution}
                    a1={a1} b1={b1} c1={c1}
                    a2={a2} b2={b2} c2={c2}
                  />
                </div>
              </Card>
            </motion.div>

            {/* Understanding Linear Systems */}
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" />
                Understanding Linear Systems
              </h2>
              
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  A system of linear equations consists of two or more equations with the same variables. 
                  When graphed, each equation represents a straight line. The solution is where these lines meet.
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-bold text-emerald-700 dark:text-emerald-400">One Solution</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Lines intersect at exactly one point. Different slopes guarantee this.
                    </p>
                    <code className="text-xs bg-background p-2 rounded block font-mono">
                      m₁ ≠ m₂
                    </code>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      <h3 className="font-bold text-blue-700 dark:text-blue-400">Infinite Solutions</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Lines coincide (same line). All points are solutions.
                    </p>
                    <code className="text-xs bg-background p-2 rounded block font-mono">
                      m₁ = m₂, b₁ = b₂
                    </code>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h3 className="font-bold text-red-700 dark:text-red-400">No Solution</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Lines are parallel. They never intersect.
                    </p>
                    <code className="text-xs bg-background p-2 rounded block font-mono">
                      m₁ = m₂, b₁ ≠ b₂
                    </code>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                  <h3 className="font-bold text-violet-700 dark:text-violet-400 mb-3 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Key Concept: The Determinant
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    The determinant (D) tells us about the solution before we solve:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• <strong>D ≠ 0:</strong> Unique solution exists (lines intersect)</li>
                    <li>• <strong>D = 0:</strong> Either infinite or no solutions (lines are parallel or identical)</li>
                  </ul>
                  <code className="text-xs bg-background p-2 rounded block font-mono mt-2">
                    D = a₁b₂ - a₂b₁ = {solution.det?.toFixed(4) || 'N/A'}
                  </code>
                </div>
              </div>
            </Card>

            {/* Solution Methods */}
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-600" />
                Solution Methods Explained
              </h2>

              <Accordion type="single" collapsible defaultValue="method-1">
                <AccordionItem value="method-1" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Award className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">Cramer's Rule</span>
                        <p className="text-xs text-muted-foreground">Uses determinants - fast and elegant</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground text-sm">
                        Cramer's Rule uses determinants to find solutions directly. It's particularly efficient for 2×2 systems.
                      </p>
                      <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium">Formulas:</p>
                        <code className="text-xs block">D = a₁b₂ - a₂b₁</code>
                        <code className="text-xs block">Dₓ = c₁b₂ - c₂b₁</code>
                        <code className="text-xs block">Dᵧ = a₁c₂ - a₂c₁</code>
                        <code className="text-xs block font-semibold text-emerald-600">x = Dₓ/D, y = Dᵧ/D</code>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Pros:</strong> Direct, no substitution needed<br />
                        <strong>Cons:</strong> Doesn't work when D = 0
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="method-2" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Target className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">Substitution Method</span>
                        <p className="text-xs text-muted-foreground">Solve for one variable, then substitute</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground text-sm">
                        Solve one equation for one variable, then substitute into the other equation.
                      </p>
                      <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium">Steps:</p>
                        <ol className="text-xs space-y-1 ml-4">
                          <li>1. Solve equation 1 for x: x = (c₁ - b₁y)/a₁</li>
                          <li>2. Substitute into equation 2</li>
                          <li>3. Solve for y</li>
                          <li>4. Substitute y back to find x</li>
                        </ol>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Pros:</strong> Intuitive, works for all systems<br />
                        <strong>Cons:</strong> Can get messy with fractions
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="method-3" className="border-0">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="text-left">
                        <span className="font-medium">Elimination Method</span>
                        <p className="text-xs text-muted-foreground">Add/subtract equations to eliminate variables</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground text-sm">
                        Multiply equations and add/subtract to eliminate one variable.
                      </p>
                      <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium">Steps:</p>
                        <ol className="text-xs space-y-1 ml-4">
                          <li>1. Multiply equations to match coefficients</li>
                          <li>2. Add or subtract to eliminate one variable</li>
                          <li>3. Solve for remaining variable</li>
                          <li>4. Back-substitute to find other variable</li>
                        </ol>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Pros:</strong> Systematic, great for larger systems<br />
                        <strong>Cons:</strong> Requires careful arithmetic
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>

            {/* Detailed Step-by-Step (Current Method) */}
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-emerald-600" />
                Step-by-Step Solution ({method === 'cramers' ? "Cramer's Rule" : method === 'substitution' ? 'Substitution' : 'Elimination'})
              </h2>

              <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                {method === 'cramers' && (
                  <>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Write in standard form</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">Our system is already in standard form ax + by = c:</p>
                          <div className="bg-secondary/50 rounded-lg p-4 font-mono text-sm space-y-1">
                            <p>{formatEquation(a1, b1, c1)}</p>
                            <p>{formatEquation(a2, b2, c2)}</p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Calculate determinant D</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">D = a₁b₂ - a₂b₁</p>
                          <div className="bg-secondary/50 rounded-lg p-4">
                            <p className="font-mono text-sm">D = ({a1})({b2}) - ({a2})({b1})</p>
                            <p className="font-mono text-sm">D = {a1 * b2} - {a2 * b1}</p>
                            <p className="font-mono text-lg font-bold text-emerald-600 mt-2">D = {solution.det?.toFixed(4)}</p>
                          </div>
                          {Math.abs(solution.det || 0) < 1e-10 ? (
                            <p className="text-sm text-destructive flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              D = 0: System has no unique solution!
                            </p>
                          ) : (
                            <p className="text-sm text-emerald-600 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4" />
                              D ≠ 0: Unique solution exists!
                            </p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {solution.type === 'unique' && (
                      <AccordionItem value="step-3" className="border-0">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                            <span className="font-medium">Calculate Dₓ and Dᵧ, then solve</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-11 space-y-4">
                            <div className="bg-secondary/50 rounded-lg p-4 space-y-3">
                              <div>
                                <p className="font-medium text-sm mb-1">For x (replace first column):</p>
                                <p className="font-mono text-xs">Dₓ = c₁b₂ - c₂b₁</p>
                                <p className="font-mono text-xs">Dₓ = ({c1})({b2}) - ({c2})({b1})</p>
                                <p className="font-mono text-sm font-semibold text-emerald-600">Dₓ = {solution.dx?.toFixed(4)}</p>
                              </div>
                              <div>
                                <p className="font-medium text-sm mb-1">For y (replace second column):</p>
                                <p className="font-mono text-xs">Dᵧ = a₁c₂ - a₂c₁</p>
                                <p className="font-mono text-xs">Dᵧ = ({a1})({c2}) - ({a2})({c1})</p>
                                <p className="font-mono text-sm font-semibold text-emerald-600">Dᵧ = {solution.dy?.toFixed(4)}</p>
                              </div>
                            </div>
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                              <p className="font-medium text-foreground mb-2 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                Final Solution:
                              </p>
                              <p className="font-mono text-lg">x = Dₓ/D = {solution.dx?.toFixed(4)}/{solution.det?.toFixed(4)} = <span className="text-emerald-600 font-bold">{solution.x?.toFixed(6)}</span></p>
                              <p className="font-mono text-lg">y = Dᵧ/D = {solution.dy?.toFixed(4)}/{solution.det?.toFixed(4)} = <span className="text-emerald-600 font-bold">{solution.y?.toFixed(6)}</span></p>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </>
                )}
              </Accordion>
            </Card>

            {/* Real World Applications */}
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-emerald-600" />
                Real-World Applications
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="font-semibold text-foreground text-sm">Economics & Business</h3>
                  <ul className="space-y-2">
                    {[
                      { title: 'Supply & Demand', desc: 'Find equilibrium price where supply equals demand' },
                      { title: 'Break-Even Analysis', desc: 'Determine when revenue equals costs' },
                      { title: 'Investment Portfolio', desc: 'Balance multiple asset allocations' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-sm font-medium text-foreground">{item.title}:</span>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="font-semibold text-foreground text-sm">Engineering & Science</h3>
                  <ul className="space-y-2">
                    {[
                      { title: 'Circuit Analysis', desc: 'Solve current and voltage in electrical circuits' },
                      { title: 'Chemical Mixing', desc: 'Calculate concentrations in solution mixtures' },
                      { title: 'Trajectory Planning', desc: 'Compute intersection of motion paths' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-sm font-medium text-foreground">{item.title}:</span>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <h3 className="font-bold text-blue-700 dark:text-blue-400 mb-2 text-sm">Example: Supply & Demand</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  A product's supply is <code className="bg-background px-1 py-0.5 rounded">S = 2p - 10</code> and demand is <code className="bg-background px-1 py-0.5 rounded">D = -3p + 40</code>, where p is price.
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setA1(2); setB1(-1); setC1(-10);  // 2p - S = -10
                    setA2(-3); setB2(-1); setC2(-40);  // -3p - D = -40
                  }}
                >
                  Load Example
                </Button>
              </div>
            </Card>

            {/* Common Mistakes */}
            <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-destructive" />
                Common Mistakes to Avoid
              </h2>
              <ul className="space-y-3">
                {[
                  { title: 'Sign Errors', desc: 'Be careful with negative signs when multiplying or subtracting equations' },
                  { title: 'Division by Zero', desc: 'Always check if determinant is zero before applying Cramer\'s Rule' },
                  { title: 'Arithmetic Mistakes', desc: 'Double-check calculations, especially with fractions and negative numbers' },
                  { title: 'Forgetting to Verify', desc: 'Always plug solutions back into original equations to verify' },
                  { title: 'Misreading the Graph', desc: 'Parallel lines mean no solution, not infinite solutions' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground text-sm">{item.title}:</span>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            {/* FAQ */}
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full">
                {[
                  {
                    q: 'When should I use which method?',
                    a: 'Cramer\'s Rule is fastest for 2×2 systems. Use Substitution when one variable has coefficient 1. Elimination is best when coefficients are easy to match.'
                  },
                  {
                    q: 'What does it mean when the determinant is zero?',
                    a: 'A zero determinant means the system either has infinite solutions (lines are identical) or no solution (lines are parallel). You need to check the equations further.'
                  },
                  {
                    q: 'Can a system have more than one solution?',
                    a: 'Yes! If the lines are identical, every point on the line is a solution - that\'s infinitely many solutions. Otherwise, there\'s either exactly one solution or none.'
                  },
                  {
                    q: 'How do I know if my answer is correct?',
                    a: 'Substitute your x and y values back into both original equations. If both equations are satisfied (left side equals right side), your answer is correct!'
                  },
                  {
                    q: 'Why do parallel lines have no solution?',
                    a: 'Parallel lines never intersect, so there\'s no point that satisfies both equations simultaneously. Mathematically, this means the system is inconsistent.'
                  },
                ].map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="font-medium text-foreground">{faq.q}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}

export default LinearEquationSolver;